import { useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { PenTool, Smartphone, FileText, CheckCircle2, XCircle, Clock, AlertTriangle, Undo2, Redo2, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { KordaroLogo, KordaroMark } from '../../../core/shared/components/KordaroLogo.js';

// === Types ===

interface SigningRequestPublic {
  id: string;
  dossierId: string;
  documentId: string;
  type: 'CANVAS' | 'ITSME';
  status: 'PENDING' | 'SIGNED' | 'EXPIRED' | 'CANCELLED';
  signedAt: string | null;
  expiresAt: string;
  createdAt: string;
  document: {
    id: string;
    naam: string;
    documentType: string | null;
    mimeType: string;
  };
  requestedBy: {
    firstName: string;
    lastName: string;
  };
  dossier: {
    id: string;
    dossierNummer: string;
    klantNaam: string;
  };
}

// === API hooks ===

function usePublicSigning(signingId: string, token: string) {
  return useQuery({
    queryKey: ['signing', 'public', signingId],
    queryFn: async (): Promise<SigningRequestPublic> => {
      const res = await fetch(`/api/signing/public/${signingId}?token=${token}`);
      if (!res.ok) throw new Error('Verzoek niet gevonden');
      return res.json();
    },
    enabled: !!signingId && !!token,
  });
}

function useSignCanvas(signingId: string, token: string) {
  return useMutation({
    mutationFn: async (signatureData: string) => {
      const res = await fetch(`/api/signing/public/${signingId}/sign-canvas?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signatureData }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Ondertekenen mislukt');
      }
      return res.json();
    },
  });
}

// === Canvas Component (simplified for klantportaal) ===

interface SignatureCanvasRef {
  toDataURL: () => string;
  isEmpty: () => boolean;
  clear: () => void;
}

function PortaalSignatureCanvas({ onReady }: { onReady: (ref: SignatureCanvasRef) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const undoStackRef = useRef<ImageData[]>([]);
  const redoStackRef = useRef<ImageData[]>([]);
  const strokeCountRef = useRef(0);

  const getCtx = useCallback(() => canvasRef.current?.getContext('2d') || null, []);

  const saveState = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    undoStackRef.current.push(imageData);
    if (undoStackRef.current.length > 30) undoStackRef.current.shift();
    redoStackRef.current = [];
  }, [getCtx]);

  const getPos = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    saveState();
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e);
    strokeCountRef.current++;
  }, [getPos, saveState]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || !lastPosRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const pos = getPos(e);
    const pressure = e.pressure > 0 ? e.pressure : 0.5;
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2 + pressure * 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
  }, [getCtx, getPos]);

  const handlePointerUp = useCallback(() => {
    isDrawingRef.current = false;
    lastPosRef.current = null;
  }, []);

  const undo = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas || undoStackRef.current.length === 0) return;
    const current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    redoStackRef.current.push(current);
    const prev = undoStackRef.current.pop()!;
    ctx.putImageData(prev, 0, 0);
  }, [getCtx]);

  const redo = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas || redoStackRef.current.length === 0) return;
    const current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    undoStackRef.current.push(current);
    const next = redoStackRef.current.pop()!;
    ctx.putImageData(next, 0, 0);
  }, [getCtx]);

  const clear = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    saveState();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokeCountRef.current = 0;
  }, [getCtx, saveState]);

  // Expose ref methods
  const refCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    (canvasRef as React.MutableRefObject<HTMLCanvasElement | null>).current = canvas;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
    }
    onReady({
      toDataURL: () => canvas.toDataURL('image/png'),
      isEmpty: () => strokeCountRef.current < 2,
      clear,
    });
  }, [clear, onReady]);

  return (
    <div>
      <canvas
        ref={refCallback}
        className="w-full bg-white border-2 border-gray-200 rounded-xl cursor-crosshair touch-none"
        style={{ height: '200px' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      <div className="flex items-center gap-2 mt-2">
        <button onClick={undo} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Ongedaan maken">
          <Undo2 size={16} />
        </button>
        <button onClick={redo} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Opnieuw">
          <Redo2 size={16} />
        </button>
        <button onClick={clear} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-auto" title="Wissen">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

// === Main Page ===

export function SigningPage() {
  const { token, signingId } = useParams<{ token: string; signingId: string }>();
  const { data: signing, isLoading, error, refetch } = usePublicSigning(signingId || '', token || '');
  const signCanvas = useSignCanvas(signingId || '', token || '');
  const [canvasRef, setCanvasRef] = useState<SignatureCanvasRef | null>(null);
  const [signed, setSigned] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleSign = async () => {
    if (!canvasRef) return;
    setValidationError('');

    if (canvasRef.isEmpty()) {
      setValidationError('Teken uw handtekening op het canvas');
      return;
    }

    const signatureData = canvasRef.toDataURL();
    try {
      await signCanvas.mutateAsync(signatureData);
      setSigned(true);
      refetch();
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Ondertekenen mislukt');
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Error / not found
  if (error || !signing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link ongeldig</h1>
          <p className="text-sm text-gray-500">
            Dit ondertekeningsverzoek bestaat niet of is verlopen.
          </p>
        </div>
      </div>
    );
  }

  // Already signed
  if (signing.status === 'SIGNED' || signed) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-brand-secondary text-white">
          <div className="max-w-lg mx-auto px-4 py-5">
            <KordaroLogo variant="ivory" className="inline-block align-middle" />
            <span className="text-white/30 text-xs ml-2">|</span>
            <span className="text-white/40 text-sm ml-2">Ondertekenen</span>
          </div>
        </header>
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Succesvol ondertekend</h1>
          <p className="text-gray-500 mb-4">
            Het document <strong>{signing.document.naam}</strong> is ondertekend.
          </p>
          <p className="text-xs text-gray-400">
            Dossier {signing.dossier.dossierNummer} — {signing.dossier.klantNaam}
          </p>
          {signing.signedAt && (
            <p className="text-xs text-gray-400 mt-1">
              Ondertekend op: {new Date(signing.signedAt).toLocaleDateString('nl-BE', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Expired
  if (signing.status === 'EXPIRED') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <Clock size={48} className="text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verzoek verlopen</h1>
          <p className="text-sm text-gray-500">
            Dit ondertekeningsverzoek is verlopen op{' '}
            {new Date(signing.expiresAt).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' })}.
            Neem contact op met uw verkoper voor een nieuw verzoek.
          </p>
        </div>
      </div>
    );
  }

  // Cancelled
  if (signing.status === 'CANCELLED') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <XCircle size={48} className="text-red-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verzoek geannuleerd</h1>
          <p className="text-sm text-gray-500">Dit ondertekeningsverzoek is geannuleerd door de winkel.</p>
        </div>
      </div>
    );
  }

  // PENDING — signing flow
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-brand-secondary text-white">
        <div className="max-w-lg mx-auto px-4 py-5">
          <div className="flex items-center gap-2">
            <KordaroLogo variant="ivory" className="inline-block align-middle" />
            <span className="text-white/30 text-xs">|</span>
            <span className="text-white/40 text-sm">Ondertekenen</span>
          </div>
          <p className="text-sm text-white/50 mt-1">
            Dossier {signing.dossier.dossierNummer} — {signing.dossier.klantNaam}
          </p>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Document info */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-brand/10 rounded-xl flex items-center justify-center">
              <FileText size={20} className="text-brand" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{signing.document.naam}</p>
              <p className="text-xs text-gray-400">
                {signing.document.documentType || 'Document'} · Aangevraagd door {signing.requestedBy.firstName} {signing.requestedBy.lastName}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Geldig tot: {new Date(signing.expiresAt).toLocaleDateString('nl-BE', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>

        {/* Canvas signing */}
        {signing.type === 'CANVAS' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <PenTool size={18} className="text-brand" />
              <h2 className="text-lg font-semibold text-gray-900">Teken uw handtekening</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Teken uw handtekening hieronder met uw vinger, stylus of muis.
            </p>

            <PortaalSignatureCanvas onReady={setCanvasRef} />

            {validationError && (
              <p className="text-xs text-red-500 mt-2">{validationError}</p>
            )}

            <button
              onClick={handleSign}
              disabled={signCanvas.isPending}
              className="w-full mt-4 py-3 bg-brand text-white rounded-xl text-sm font-semibold hover:bg-[#b89a52] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={18} />
              {signCanvas.isPending ? 'Bezig met ondertekenen...' : 'Onderteken document'}
            </button>
          </div>
        )}

        {/* itsme signing (placeholder) */}
        {signing.type === 'ITSME' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone size={32} className="text-blue-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Onderteken met itsme</h2>
            <p className="text-sm text-gray-500 mb-4">
              itsme biedt een gekwalificeerde elektronische handtekening die rechtsgeldig is onder de eIDAS-verordening.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Deze functionaliteit wordt binnenkort beschikbaar. U ontvangt een notificatie zodra u met itsme kunt ondertekenen.
            </p>
            <div className="bg-blue-50 rounded-xl p-4 text-left">
              <h3 className="text-sm font-medium text-blue-700 mb-2">Hoe werkt itsme ondertekening?</h3>
              <ol className="text-xs text-blue-600 space-y-1.5 list-decimal list-inside">
                <li>U klikt op "Onderteken met itsme"</li>
                <li>U wordt doorverwezen naar de itsme app</li>
                <li>U bevestigt met uw PIN of biometrie</li>
                <li>Het document wordt automatisch ondertekend</li>
              </ol>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 pb-4">
          Beveiligd door Kordaro · Uw handtekening wordt veilig opgeslagen
        </p>
      </div>
    </div>
  );
}
